import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Star, TrendingDown } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { getDeals } from "@/lib/api";
import { euro } from "@/lib/format";

export const FlashDeals = () => {
  const { t } = useApp();
  const [deals, setDeals] = useState([]);

  useEffect(() => {
    getDeals().then(setDeals).catch(() => {});
  }, []);

  if (!deals.length) return null;
  const hero = deals[0];
  const rest = deals.slice(1, 4);
  const pct = (d) => (d.market_high ? Math.round((d.savings / d.market_high) * 100) : 0);

  return (
    <section data-testid="flash-deals-section" className="relative bg-terracotta py-20 lg:py-28">
      <div className="grain absolute inset-0 opacity-40" />
      <div className="relative mx-auto max-w-7xl px-5 lg:px-8">
        <div className="mb-10 flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="overline text-white/70">{t("sections.deals_over")}</p>
            <h2 className="mt-2 font-serif text-4xl font-medium text-white lg:text-5xl">
              {t("sections.deals_title")}
            </h2>
          </div>
          <Link to="/stays?sort=savings" data-testid="deals-see-all" className="flex items-center gap-1 font-semibold text-white/90 hover:text-white">
            {t("nav.deals")} <ArrowUpRight size={18} />
          </Link>
        </div>

        <div className="grid gap-6 lg:grid-cols-2">
          {/* Biggest saving */}
          <motion.div
            initial={{ opacity: 0, scale: 0.97 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            data-testid="flash-deal-card"
            className="relative flex flex-col justify-end overflow-hidden rounded-3xl min-h-[420px]"
          >
            <img src={hero.images?.[0]} alt={hero.name} className="absolute inset-0 h-full w-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-petrol-dark via-petrol/40 to-transparent" />
            <Link to={`/stay/${hero.slug}`} className="absolute inset-0 z-10" aria-label={hero.name} />
            <div className="pointer-events-none relative z-20 p-7 text-white">
              <span className="inline-flex items-center gap-1.5 rounded-none bg-white/20 px-3 py-1 text-xs font-bold uppercase tracking-wide backdrop-blur">
                <TrendingDown size={13} /> −{pct(hero)}% {t("card.save")} {euro(hero.savings)}
              </span>
              <h3 className="mt-4 font-serif text-3xl font-semibold">{hero.name}</h3>
              <p className="text-white/80">{hero.zone_name}</p>
              <div className="mt-4 flex items-end gap-3">
                <span className="text-white/50 line-through">{euro(hero.market_high)}</span>
                <span className="font-serif text-4xl font-bold">{euro(hero.best_price)}</span>
                <span className="mb-1 text-sm text-white/70">{t("card.per_night")}</span>
              </div>
              <div className="mt-4 flex flex-wrap items-center gap-4">
                {hero.rating && (
                  <span className="flex items-center gap-1.5 rounded-none bg-petrol-dark/70 px-3 py-1.5 text-sm">
                    <Star size={13} className="fill-gold text-gold" /> {hero.rating} · {hero.reviews_count} {t("card.reviews")}
                  </span>
                )}
                <Link
                  to={`/stay/${hero.slug}`}
                  className="pointer-events-auto relative z-30 rounded-none bg-white px-5 py-2.5 text-sm font-semibold text-petrol transition-transform hover:scale-105"
                >
                  {t("card.view")}
                </Link>
              </div>
            </div>
          </motion.div>

          {/* Other savings */}
          <div className="flex flex-col gap-4">
            {rest.map((d, i) => (
              <motion.div
                key={d.slug}
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
              >
                <Link
                  to={`/stay/${d.slug}`}
                  data-testid="flash-deal-card"
                  className="hover-lift flex items-center gap-4 rounded-2xl bg-sand-card p-3"
                >
                  <img src={d.images?.[0]} alt={d.name} className="h-24 w-28 rounded-xl object-cover" />
                  <div className="flex-1">
                    <p className="text-xs font-semibold uppercase tracking-wide text-terracotta">{d.zone_name}</p>
                    <h4 className="font-serif text-lg font-semibold text-petrol">{d.name}</h4>
                    <div className="mt-1 flex items-center gap-1 text-xs text-petrol/60">
                      <Star size={11} className="fill-gold text-gold" /> {d.rating} · {d.reviews_count} {t("card.reviews")}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs text-petrol/40 line-through">{euro(d.market_high)}</div>
                    <div className="font-serif text-2xl font-bold text-petrol">{euro(d.best_price)}</div>
                    <div className="text-xs font-semibold text-terracotta">{t("card.save")} {euro(d.savings)}</div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
